/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState } from 'react';
import proptypes from 'prop-types';
import {
  Alert, Badge, Card, CardMedia, Link, Typography
} from '@one-thd/sui-atomic-components';
import { Paint, CounterTop, Drill } from '@one-thd/sui-icons';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { ShareProject } from '@thd-olt-component-react/project-forms';
import { useImpression } from '@thd-olt-component-react/impression';
import { ProjectTilePlaceholder } from '../project-tile/ProjectTilePlaceholder';
import { useMatchMedia } from '../../hooks/useMatchMedia';
import { triggerAnalytics } from '../../helpers';

/**
 * ProjectCardList - Presentational project card list component
 * Displays a single project card with project information and up to 3 thumbnails
 * The consuming component controls layout, sizing, and arrangement (row/column/grid)
 */
const {
  func, shape, string, arrayOf, bool, number
} = proptypes;

const ProTile = ({
  project,
  projectId,
  position,
  maxThumbnails,
  loading,
  impressionName
}) => {
  // const isDesktop = useMatchMedia('(min-width: 768px)', 768);
  // const [successMessage, setSuccessMessage] = useState('');
  // const [failureMessage, setFailureMessage] = useState('');

  const { ref, clickID } = useImpression({
    data: {
      id: projectId,
      name: impressionName,
      component: impressionName,
      position,
      category: project?.status || '',
      type: 'content'
    }
  });

  // Comment out share feature related code until fixed and reinstated
  // const handleSuccess = (message) => {
  //   setFailureMessage('');
  //   setSuccessMessage(message);
  // };

  // const handleFailure = (error) => {
  //   setSuccessMessage('');
  //   setFailureMessage(error);
  // };

  // Show placeholder when loading and no project data
  if (!project && loading) return <ProjectTilePlaceholder />;
  if (!project) return null;

  const {
    id,
    projectName,
    status,
    createdByName,
    images = [],
    numberOfItems = 0
  } = project;

  const url = `/projects/${id}`;
  const isActive = status === 'active';
  const statusInfo = {
    text: isActive ? 'Active' : 'Archived',
    color: isActive ? 'info' : 'medium',
    textColor: isActive ? 'primary' : 'subtle', // Active uses primary text, Archived uses subtle text
  };

  // Display only the number of thumbnails specified (default 3 for UI specification)
  const displayImages = images?.slice(0, maxThumbnails) || [];
  const remainingCount = numberOfItems - displayImages.length;

  const handleAddMaterialsClick = () => {
    const payload = {
      action: 'button-click',
      id: projectId || '',
      component: 'empty project add materials gray box click',
    };
    triggerAnalytics('projects.interaction', payload);
  };

  return (
    <div
      ref={ref}
      // eslint-disable-next-line react/no-unknown-property
      clickid={clickID}
    >
      <Card
        className="sui-w-full sui-h-full [&>div]:sui-justify-between [&>div]:sui-h-full"
        data-component="ProjectCard"
        data-testid="project-card"
      >
        <div className="sui-flex sui-flex-col sui-gap-4">
          {/* Header section with badge and share icon */}
          <div className="sui-flex sui-justify-between">
            <Badge size="small" variant="subtle" color={statusInfo.color}>
              <span className="sui-capitalize">Status: {statusInfo.text}</span>
            </Badge>
            {/* <div className="sui-flex sui-items-center">
            <QueryProvider cacheKey={`share-project-${id}`}>
              <ShareProject
                project={{
                  id,
                  defaultListId: project.defaultListId,
                  name: projectName,
                  public: project.public,
                }}
                showLink
                onSuccessMessage={handleSuccess}
                onFailureMessage={handleFailure}
              />
            </QueryProvider>
          </div> */}
          </div>

          {/* Project Details Section */}
          <div className="sui-flex sui-flex-col sui-gap-0.5">
            {/* Project Title */}
            <div className="sui-flex sui-items-start sui-gap-2">
              <Typography variant="body" weight="bold" color={statusInfo.textColor} className="sui-shrink-0">
                Project:
              </Typography>
              <Link href={url} className="sui-underline">
                <Typography variant="body" color={statusInfo.textColor}>
                  {projectName}
                </Typography>
              </Link>
            </div>

            {/* {(successMessage || failureMessage) && (
              <div className="sui-my-1">
                {successMessage && (
                  <Alert
                    status="info"
                    onClose={(el) => {
                      el?.currentTarget?.closest('[role="alert"]')?.remove();
                    }}
                  >
                    {successMessage}
                  </Alert>
                )}
                {failureMessage && (
                  <Alert
                    status="error"
                    onClose={(el) => {
                      el?.currentTarget?.closest('[role="alert"]')?.remove();
                    }}
                  >
                    {failureMessage}
                  </Alert>
                )}
              </div>
            )} */}

            {/* Created By - Only show if createdByName exists */}
            {createdByName && (
              <div className="sui-flex sui-items-start sui-gap-2 sui-pt-1">
                <Typography variant="body" weight="bold" color={statusInfo.textColor} className="sui-shrink-0">
                  Created By:
                </Typography>
                <Typography variant="body" color={statusInfo.textColor}>
                  {createdByName}
                </Typography>
              </div>
            )}
          </div>
        </div>

        {/* Project Materials Section */}
        <div className="sui-flex sui-flex-col sui-gap-1">
          {displayImages.length > 0 ? (
            <>
              <Typography variant="body" weight="bold" color={statusInfo.textColor}>
                Project Materials:
              </Typography>
              {/* Thumbnails - Show up to 3 images plus count */}
              <div className="sui-flex sui-gap-3 sui-items-center sui-mt-1">
                {displayImages.map((imageUrl, index) => (
                  <div
                    key={`thumbnail-${index}`}
                    className="sui-w-14 sui-h-14
                  sui-flex sui-items-center sui-justify-center
                  sui-overflow-hidden sui-rounded-sm"
                  >
                    <CardMedia
                      src={imageUrl}
                      alt={`Product ${index + 1}`}
                      className="sui-w-full sui-h-full sui-object-cover"
                    />
                  </div>
                ))}

                {/* Show remaining count if there are more items */}
                {remainingCount > 0 && (
                  <div
                    className="sui-w-14 sui-h-14
                  sui-bg-strong sui-flex sui-items-center
                  sui-justify-center sui-rounded-sm"
                  >
                    <Typography variant="body" color="heading">
                      +{remainingCount}
                    </Typography>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Empty state - show "Add materials to your project" message with icons */
            <div
              className="sui-flex sui-items-center
            sui-justify-center sui-gap-4 sui-bg-subtle
            sui-rounded-sm sui-p-3 @sm:sui-py-10"
              onClick={handleAddMaterialsClick}
            >
              <Typography variant="body-sm" color="subtle">
                Add materials to your project
              </Typography>
              <div className="sui-flex sui-gap-4 sui-items-center">
                <Drill size="regular" color="subtle" />
                <CounterTop size="regular" color="subtle" />
                <Paint size="regular" color="subtle" />
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

ProTile.displayName = 'ProTile';

ProTile.propTypes = {
  projectId: string.isRequired,
  project: shape({
    id: string,
    projectName: string,
    defaultListId: string,
    public: bool,
    status: string,
    modifyDateTime: string,
    createdBy: string,
    createdByName: string,
    isCreatedByTHDAssociate: bool,
    numberOfItems: number,
    images: arrayOf(string)
  }),
  position: number,
  loading: bool,
  maxThumbnails: number,
  impressionName: string,
};

ProTile.defaultProps = {
  project: null,
  maxThumbnails: 3,
  loading: false,
  position: 0,
  impressionName: 'ProjectsOverview'
};

export { ProTile };
